import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s, tableTokens } from '@/theme/tokens';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Box } from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import type { ReactElement, ReactNode } from 'react';

export type SettingsTabbedHeaderTabDefinition<TTab extends string> = {
  value: TTab;
  label: ReactNode;
};

type SettingsTabbedHeaderProps<TTab extends string> = {
  title: string;
  subtitle: string;
  activeTab: TTab;
  tabs: ReadonlyArray<SettingsTabbedHeaderTabDefinition<TTab>>;
  tabsAriaLabel: string;
  onTabChange: (nextValue: TTab) => void;
};

export const SettingsTabbedHeader = <TTab extends string>({
  title,
  subtitle,
  activeTab,
  tabs,
  tabsAriaLabel,
  onTabChange,
}: SettingsTabbedHeaderProps<TTab>): ReactElement => {
  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title={title} subtitle={subtitle} align="start" />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: s('lg'), mb: s('sm') }}>
        <Tabs
          value={activeTab}
          onChange={(_event, nextValue) => {
            if (typeof nextValue === 'string') {
              onTabChange(nextValue as TTab);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          preserveScrollOnChange={false}
          aria-label={tabsAriaLabel}
          sx={(theme) => ({
            maxWidth: '100%',
            minHeight: `calc(${tableTokens.rowHeight} + ${s('xs')})`,
            px: 0,
            '& .MuiTabs-flexContainer': { gap: s('sm') },
            '& .MuiTabs-indicator': {
              backgroundColor: resolveSemanticColor(theme, 'textPrimary'),
              height: '1px',
            },
          })}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              sx={(theme) => ({
                ...theme.typography.body1,
                color: 'text.secondary',
                fontWeight: theme.typography.subtitle1.fontWeight,
                height: `calc(${tableTokens.rowHeight} + ${s('xs')})`,
                minHeight: `calc(${tableTokens.rowHeight} + ${s('xs')})`,
                px: s('md'),
                '&.Mui-selected': {
                  color: 'text.primary',
                  fontWeight: theme.typography.subtitle1.fontWeight,
                },
              })}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
};
